import React, { useState, useEffect } from 'react';
import { AppStateProvider, useAppState, appActions } from './hooks/useAppState';
import AppRouter from './router/AppRouter';
import { localStorageUtils } from './utils/localStorage';
import { defaultUserPreferences } from './data/defaultUserPreferences';
import { LocationInput } from './types';
import './utils/resetOnboarding'; // Import for testing utility
import './utils/geolocationMock'; // Import for geolocation mocking in development
import './App.css';

function AppContent() {
  const { dispatch } = useAppState();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<'generate' | 'route-results' | 'navigation'>('generate');
  const [routeData, setRouteData] = useState<{
    startLocation: LocationInput;
    endLocation: LocationInput;
    routeCoordinates?: [number, number][];
    requestId?: number; // Request ID from backend route init
    routeStats?: {
      total_distance?: number;
      total_crossings?: number;
      distance_sidewalk?: number;
      distance_bikelane?: number;
      total_duration?: number;
      time_sidewalk?: number;
      time_bikelane?: number;
    };
  } | null>(null);

  useEffect(() => {
    // Load user preferences from localStorage on app start
    const savedPreferences = localStorageUtils.getUserPreferences();
    
    if (savedPreferences) {
      // Update preferences with saved data
      dispatch(appActions.updatePreferences(savedPreferences));
    } else {
      // Set default preferences
      dispatch(appActions.updatePreferences(defaultUserPreferences));
    }
    
    setIsLoading(false);
  }, [dispatch]);


  const handleRouteGenerated = (
    startLocation: LocationInput,
    endLocation: LocationInput,
    routeCoordinates?: [number, number][],
    requestId?: number,
    routeStats?: {
      total_distance?: number;
      total_crossings?: number;
      distance_sidewalk?: number;
      distance_bikelane?: number;
      total_duration?: number;
      time_sidewalk?: number;
      time_bikelane?: number;
    }
  ) => {
    // Route is generated, show route results page
    setRouteData({ startLocation, endLocation, routeCoordinates, requestId, routeStats });
    setCurrentPage('route-results');
  };

  const handleBackToGenerate = () => {
    setCurrentPage('generate');
    setRouteData(null);
  };

  const handleStartNavigation = (routeCoordinates: [number, number][]) => {
    if (routeData) {
      setRouteData({ ...routeData, routeCoordinates });
    }
    setCurrentPage('navigation');
  };

  const handleStopNavigation = () => {
    setCurrentPage('route-results');
  };

  const handleBackFromNavigation = () => {
    setCurrentPage('route-results');
  };


  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const handleRouteCompleted = () => {
    // Route completed, could navigate to a completion page or back to generate
    setCurrentPage('generate');
    setRouteData(null);
  };

  const handleBackToOnboarding = () => {
    // Force re-render by updating state
    dispatch({ type: 'RESET_ONBOARDING' });
  };

  return (
    <AppRouter
      currentPage={currentPage}
      routeData={routeData}
      onRouteGenerated={handleRouteGenerated}
      onBackToGenerate={handleBackToGenerate}
      onStartNavigation={handleStartNavigation}
      onStopNavigation={handleStopNavigation}
      onBackFromNavigation={handleBackFromNavigation}
      onRouteCompleted={handleRouteCompleted}
      onBackToOnboarding={handleBackToOnboarding}
    />
  );
}

function App() {
  return (
    <AppStateProvider>
      <div className="App">
        <AppContent />
      </div>
    </AppStateProvider>
  );
}

export default App;
